import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2, ShieldCheck, FileSignature } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Row = ({ label, value }) => (
  <>
    <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold">{label}</p>
    <p className="text-white text-sm break-words">{value}</p>
  </>
);

const SignBriefPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [typedName, setTypedName] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setErr('No token'); setLoading(false); return; }
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/public/sign-brief/${encodeURIComponent(token)}`);
        if (r.ok) {
          const d = await r.json();
          setBrief(d);
          if (d.signature?.signed) setTypedName(d.signature.typed_name || '');
        } else {
          const e = await r.json().catch(() => ({}));
          setErr(e.detail || 'Brief not found or link expired');
        }
      } catch { setErr('Network error'); }
      setLoading(false);
    })();
  }, [token]);

  const submit = async () => {
    if (typedName.trim().length < 2) {
      toast.error('Please type your full name to sign'); return;
    }
    if (!consent) {
      toast.error('Please tick the consent box'); return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/public/sign-brief/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ typed_name: typedName.trim(), consent: true }),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        setBrief(prev => ({ ...prev, signature: { signed: true, typed_name: d.typed_name, signed_at: d.signed_at } }));
        toast.success('Brief signed — thank you.');
      } else {
        toast.error(d.detail || 'Submission failed');
      }
    } catch { toast.error('Network error'); }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1a14] flex items-center justify-center text-white/50">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your brief…
      </div>
    );
  }

  if (err || !brief) {
    return (
      <div className="min-h-screen bg-[#0a1a14] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center" data-testid="sign-brief-error">
          <AlertCircle className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
          <h1 className="text-white text-2xl font-bold mb-2">Link not valid</h1>
          <p className="text-white/50 text-sm mb-6">{err || 'This signing link may have expired.'}</p>
          <Link to="/bespoke-sourcing">
            <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941E]" data-testid="sign-brief-submit-new">
              Submit a new brief
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const signed = brief.signature?.signed;
  const portalLink = `/my-brief?token=${encodeURIComponent(token)}`;

  return (
    <div className="min-h-screen bg-[#0a1a14] text-white">
      <div className="max-w-3xl mx-auto px-6 py-12 sm:py-16">
        {/* Brand mark */}
        <div className="text-center mb-10" data-testid="sign-brief-page">
          <p className="text-[#D4AF37] text-[10px] tracking-[0.4em] font-bold">BLACK STAR · PI</p>
          <p className="text-white/40 text-[10px] tracking-[0.3em] mt-1">CONFIDENTIAL · CURATED · INVESTOR-LED</p>
        </div>

        {/* Title */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">
            {signed ? 'Brief Confirmed' : 'Confirm Your Brief'}
          </h1>
          <p className="text-white/55 text-sm max-w-xl mx-auto">
            {signed
              ? 'Thank you. Your acquisition criteria are formally on record with our team.'
              : 'Please review your acquisition criteria below. Once you’re happy, add your typed signature to lock the search.'}
          </p>
        </div>

        {/* Already signed banner */}
        {signed && (
          <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-5 mb-8 flex items-start gap-3" data-testid="sign-brief-already-signed">
            <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-emerald-100 font-semibold text-sm">Signed by {brief.signature.typed_name}</p>
              <p className="text-emerald-100/70 text-xs mt-0.5">
                On {brief.signature.signed_at ? new Date(brief.signature.signed_at).toLocaleString() : 'record'}.
                A copy was emailed to you. You can revisit your <Link to={portalLink} className="text-[#D4AF37] underline">private portal</Link> any time.
              </p>
            </div>
          </div>
        )}

        {/* Brief recap */}
        <div className="bg-white/[0.025] border border-white/10 rounded-xl p-6 mb-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3.5">
          <Row label="Investor"             value={brief.full_name || '—'} />
          <Row label="Email"                value={brief.email     || '—'} />
          {brief.country_residence && <Row label="Country of residence" value={brief.country_residence} />}
          {brief.preferred_countries?.length > 0 && (
            <Row label="Target countries" value={brief.preferred_countries.join(' · ')} />
          )}
          {brief.preferred_cities    && <Row label="Target cities"      value={brief.preferred_cities} />}
          {brief.asset_classes?.length > 0 && (
            <Row label="Asset classes" value={brief.asset_classes.join(' · ')} />
          )}
          {(brief.strategies?.length > 0 || brief.other_strategy) && (
            <Row label="Strategies" value={[...(brief.strategies || []), brief.other_strategy && `Other: ${brief.other_strategy}`].filter(Boolean).join(' · ')} />
          )}
          {brief.investment_budget && <Row label="Investment budget" value={brief.investment_budget} />}
          {brief.target_yield      && <Row label="Target yield"      value={brief.target_yield} />}
          {brief.capital_growth    && <Row label="Capital growth"    value={brief.capital_growth} />}
          {brief.holding_period    && <Row label="Holding period"    value={brief.holding_period} />}
          {brief.timeline          && <Row label="Timeline"          value={brief.timeline} />}
          {brief.additional_notes && (
            <div className="sm:col-span-2">
              <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-1">Additional notes</p>
              <p className="text-white/85 text-sm whitespace-pre-wrap">{brief.additional_notes}</p>
            </div>
          )}
        </div>

        {/* Signature card */}
        {!signed && (
          <div className="bg-gradient-to-br from-[#0a1a14] via-[#0a1a14] to-[#D4AF37]/5 border border-[#D4AF37]/30 rounded-xl p-6" data-testid="sign-brief-signature-card">
            <div className="flex items-start gap-2.5 mb-5">
              <FileSignature className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-white text-lg font-bold">Add your signature</h2>
                <p className="text-white/55 text-xs mt-0.5">Type your full legal name exactly as it should appear on record.</p>
              </div>
            </div>

            <label className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-semibold">Full name</label>
            <input
              type="text"
              value={typedName}
              onChange={e => setTypedName(e.target.value)}
              placeholder="e.g. Daniel Adeyemi"
              autoComplete="name"
              className="w-full mt-1.5 bg-[#0a1a14] border border-white/15 focus:border-[#D4AF37]/60 rounded-md px-4 py-3 text-white focus:outline-none text-lg font-serif italic"
              data-testid="sign-brief-typed-name"
            />

            <label className="flex items-start gap-3 mt-5 cursor-pointer">
              <input
                type="checkbox"
                checked={consent}
                onChange={e => setConsent(e.target.checked)}
                className="mt-1 accent-[#D4AF37] w-4 h-4 flex-shrink-0"
                data-testid="sign-brief-consent"
              />
              <span className="text-white/70 text-sm leading-relaxed">
                I confirm the acquisition criteria above accurately reflect my requirements. I understand my typed signature carries the same effect as a handwritten signature, and that Black Star PI will proceed to source opportunities matched to these criteria.
              </span>
            </label>

            <Button
              onClick={submit}
              disabled={submitting || typedName.trim().length < 2 || !consent}
              className="w-full mt-6 bg-[#D4AF37] text-black hover:bg-[#B8941E] disabled:opacity-40 disabled:cursor-not-allowed h-12 text-sm font-bold tracking-widest"
              data-testid="sign-brief-submit"
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              {submitting ? 'SIGNING…' : 'CONFIRM & SIGN'}
            </Button>

            <p className="text-white/30 text-[10px] mt-4 text-center">
              Your signature is timestamped and recorded with your IP address as an auditable record.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12">
          <Link to={portalLink} className="text-[#D4AF37] text-xs hover:underline" data-testid="sign-brief-portal-link">
            View my private brief portal →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignBriefPage;
